// DocumentPayments.jsx
//
// AR payment schedule for Invoice / Proposal (DOCUMENT_PAYMENT children).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

// Context
import { useBase } from 'context/BaseContext';
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Services
import { updateNode } from 'services/nodes/NodeWriteService';

// Utilities
import { formatPrice } from 'utilities/Currency';
import { buildNodeFieldPatch } from 'utilities/NodeFieldData';

// Local
import { updateBase } from 'services/bases/BaseWriteService';
import {
  buildDefaultScheduleSettingsPatch,
  getDocumentPaymentSettings,
} from 'services/bases/documentPaymentBaseSettings';
import { createDocumentPayment } from 'nodetypes/documents/payments/createDocumentPayment';
import { DOCUMENT_PAYMENT_TYPE_KEY } from 'nodetypes/documents/payments/documentPaymentKeys';
import {
  filterDocumentPayments,
  getDocumentPaymentRowModel,
  isDocumentPaymentOpenStatus,
  readDocumentPaymentField,
} from 'nodetypes/documents/payments/documentPaymentsModel';
import LogDocumentPaymentModal from 'nodetypes/documents/payments/LogDocumentPaymentModal';
import { writeBackDocumentPaymentRollups } from 'nodetypes/documents/payments/writeBackDocumentPaymentRollups';
import useDocumentRelatedNodes from 'nodetypes/documents/hooks/useDocumentRelatedNodes';
import { INVOICE_ITEM_TYPE_KEY } from 'nodetypes/invoice/view/invoiceGeneralInfoModel';
import { computeInvoiceItemsRollups } from 'nodetypes/invoice/view/invoiceItemsModel';
import { PROPOSAL_ITEM_TYPE_KEY } from 'nodetypes/proposal/view/proposalGeneralInfoModel';
import { computeProposalItemsRollups } from 'nodetypes/proposal/view/proposalItemsModel';

// CSS
import './DocumentPayments.css';

// Components
import Button from 'components/button/Button';
import EnumPillSelect from 'components/enumpillselect/EnumPillSelect';
import IconButton from 'components/iconbutton/IconButton';

function cleanString(value) {
  return String(value || '').trim();
}

function formatMoneyCell(value) {
  if (value === null || value === undefined || value === '') {
    return <span className="document-payments-muted">—</span>;
  }
  return formatPrice(value);
}

function getPaymentSchemaField(paymentType, fieldName) {
  const fields = Array.isArray(paymentType?.schema?.fields) ? paymentType.schema.fields : [];
  return fields.find((field) => cleanString(field?.name) === fieldName) || null;
}

function resolveItemTypeKey(parentTypeKey) {
  if (parentTypeKey === 'PROPOSAL') return PROPOSAL_ITEM_TYPE_KEY;
  return INVOICE_ITEM_TYPE_KEY;
}

function computeParentRollups(parentTypeKey, items, node, nodeType, payments) {
  if (parentTypeKey === 'PROPOSAL') {
    return computeProposalItemsRollups(items, node, nodeType, payments);
  }
  return computeInvoiceItemsRollups(items, node, nodeType, payments);
}

/**
 * DocumentPayments
 *
 * Payment schedule / ledger for Invoice or Proposal.
 */
const DocumentPayments = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  structureLocked = false,
  canLogPayments = true,
  neighborNodes: neighborNodesProp = null,
  onRelatedNodeOpen = null,
  previewMode = false,
}) => {
  const { activeBase } = useBase();
  const { user } = useUser();
  const { getType } = useNodeTypes();
  const language = useCurrentLanguage();

  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [logPayment, setLogPayment] = useState(null);
  const writeBackTimerRef = useRef(null);

  const baseKey = cleanString(activeBase?.key || node?.baseKey);
  const parentKey = cleanString(node?.key);
  const parentTypeKey = cleanString(nodeType?.key || node?.type).toUpperCase();
  const canEditStructure = Boolean(!readOnly && !previewMode && !structureLocked);
  const canEdit = canEditStructure;
  const canLog = Boolean(!readOnly && !previewMode && canLogPayments);
  const canOpenPayment = typeof onRelatedNodeOpen === 'function' && !previewMode;

  const subscribedNeighbors = useDocumentRelatedNodes(
    neighborNodesProp == null ? parentKey : null,
    neighborNodesProp == null ? baseKey : null,
  );
  const neighborNodes = neighborNodesProp == null ? subscribedNeighbors : neighborNodesProp;

  const paymentType = useMemo(() => getType(DOCUMENT_PAYMENT_TYPE_KEY), [getType]);
  const statusField = useMemo(
    () => getPaymentSchemaField(paymentType, 'status'),
    [paymentType],
  );

  const payments = useMemo(
    () => filterDocumentPayments(neighborNodes),
    [neighborNodes],
  );

  const itemTypeKey = resolveItemTypeKey(parentTypeKey);
  const items = useMemo(() => (
    (Array.isArray(neighborNodes) ? neighborNodes : [])
      .filter((row) => cleanString(row?.type).toUpperCase() === itemTypeKey)
  ), [itemTypeKey, neighborNodes]);

  const rows = useMemo(
    () => payments.map((payment) => ({
      payment,
      model: getDocumentPaymentRowModel(payment, paymentType, language),
    })),
    [language, paymentType, payments],
  );

  const rollups = useMemo(
    () => computeParentRollups(parentTypeKey, items, node, nodeType, payments),
    [items, node, nodeType, parentTypeKey, payments],
  );

  const defaultSchedule = useMemo(
    () => getDocumentPaymentSettings(activeBase).defaultSchedule,
    [activeBase],
  );

  const runWriteBack = useCallback(async () => {
    if (!node?.key || previewMode) return;
    try {
      await writeBackDocumentPaymentRollups({
        parentNode: node,
        parentType: nodeType,
        items,
        payments,
      });
    } catch (error) {
      console.warn('[DocumentPayments] write-back failed', error);
    }
  }, [items, node, nodeType, payments, previewMode]);

  const scheduleWriteBack = useCallback(() => {
    if (writeBackTimerRef.current) clearTimeout(writeBackTimerRef.current);
    writeBackTimerRef.current = setTimeout(() => {
      writeBackTimerRef.current = null;
      runWriteBack();
    }, 250);
  }, [runWriteBack]);

  useEffect(() => () => {
    if (writeBackTimerRef.current) clearTimeout(writeBackTimerRef.current);
  }, []);

  // Keep header rollups in sync when payment children change.
  useEffect(() => {
    if (!canEdit || !payments.length) return undefined;
    scheduleWriteBack();
    return undefined;
  }, [canEdit, payments, scheduleWriteBack]);

  // Recompute percent-based scheduled amounts when document total changes.
  useEffect(() => {
    if (!canEdit || !Number.isFinite(Number(rollups.total))) return undefined;

    const total = Number(rollups.total) || 0;
    payments.forEach((payment) => {
      const status = readDocumentPaymentField(payment, 'status');
      if (!isDocumentPaymentOpenStatus(status)) return;
      const basis = cleanString(readDocumentPaymentField(payment, 'amountBasis')).toLowerCase();
      if (basis !== 'percent') return;
      const percent = Number(readDocumentPaymentField(payment, 'percentOfTotal'));
      if (!Number.isFinite(percent)) return;
      const nextAmount = Math.round((total * percent) / 100);
      const currentAmount = Number(readDocumentPaymentField(payment, 'amount'));
      if (currentAmount === nextAmount) return;
      const patch = buildNodeFieldPatch(
        payment,
        { name: 'amount', source: 'document_payment', type: 'money' },
        DOCUMENT_PAYMENT_TYPE_KEY,
        nextAmount,
      );
      if (patch) updateNode(payment.key, patch);
    });
    return undefined;
  }, [canEdit, payments, rollups.total]);

  const handlePaymentChange = useCallback((paymentKey, patch) => {
    if (!canEdit || !paymentKey || !patch) return undefined;
    const result = updateNode(paymentKey, patch);
    scheduleWriteBack();
    return result;
  }, [canEdit, scheduleWriteBack]);

  const handleRowOpen = useCallback((payment) => {
    if (!canOpenPayment || !payment?.key) return;
    onRelatedNodeOpen(payment);
  }, [canOpenPayment, onRelatedNodeOpen]);

  const handleSchedule = useCallback(async ({
    title = null,
    amountBasis = 'fixed',
    percentOfTotal = null,
  } = {}) => {
    if (!canEdit || scheduleBusy || !node?.key || !user?.key) return;

    setScheduleBusy(true);
    try {
      const remaining = Math.max(0, Number(rollups.balanceDue) || 0);
      let defaultAmountCents = remaining > 0 ? remaining : null;
      if (amountBasis === 'percent' && percentOfTotal != null) {
        const total = Number(rollups.total) || 0;
        defaultAmountCents = Math.round((total * Number(percentOfTotal)) / 100);
      }

      const created = await createDocumentPayment({
        parentNode: node,
        user,
        baseKey: baseKey || null,
        paymentType,
        parentType: nodeType,
        existingCount: payments.length,
        defaultAmountCents,
        percentOfTotal: amountBasis === 'percent' ? percentOfTotal : null,
        amountBasis,
        title,
      });
      scheduleWriteBack();
      if (canOpenPayment && created?.key) {
        onRelatedNodeOpen(created);
      }
    } catch (error) {
      toast.error(error?.message || 'Could not schedule a payment.', {
        autoClose: 4000,
      });
    } finally {
      setScheduleBusy(false);
    }
  }, [
    baseKey,
    canEdit,
    canOpenPayment,
    node,
    nodeType,
    onRelatedNodeOpen,
    paymentType,
    payments.length,
    rollups.balanceDue,
    rollups.total,
    scheduleBusy,
    scheduleWriteBack,
    user,
  ]);

  const handleMove = useCallback(async (payment, direction) => {
    if (!canEdit || !payment?.key) return;
    const index = payments.findIndex((row) => row.key === payment.key);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || swapIndex < 0 || swapIndex >= payments.length) return;

    const other = payments[swapIndex];
    const thisOrder = Number(readDocumentPaymentField(payment, 'sortOrder'));
    const otherOrder = Number(readDocumentPaymentField(other, 'sortOrder'));
    const leftOrder = Number.isFinite(thisOrder) ? thisOrder : (index + 1) * 10;
    const rightOrder = Number.isFinite(otherOrder) ? otherOrder : (swapIndex + 1) * 10;

    const patchA = buildNodeFieldPatch(
      payment,
      { name: 'sortOrder', source: 'document_payment', type: 'number' },
      DOCUMENT_PAYMENT_TYPE_KEY,
      rightOrder,
    );
    const patchB = buildNodeFieldPatch(
      other,
      { name: 'sortOrder', source: 'document_payment', type: 'number' },
      DOCUMENT_PAYMENT_TYPE_KEY,
      leftOrder,
    );
    try {
      if (patchA) await updateNode(payment.key, patchA);
      if (patchB) await updateNode(other.key, patchB);
    } catch (error) {
      toast.error(error?.message || 'Could not reorder payment.', { autoClose: 3000 });
    }
  }, [canEdit, payments]);

  const handleLogged = useCallback(() => {
    scheduleWriteBack();
  }, [scheduleWriteBack]);

  const handleSaveDefaultSchedule = useCallback(async () => {
    if (!canEditStructure || !baseKey || !payments.length) return;
    try {
      const patch = buildDefaultScheduleSettingsPatch(
        payments,
        readDocumentPaymentField,
        activeBase?.documentPayments,
      );
      await updateBase(baseKey, patch);
      toast.success('Default payment schedule saved for this base.', { autoClose: 3000 });
    } catch (error) {
      toast.error(error?.message || 'Could not save default schedule.', { autoClose: 4000 });
    }
  }, [activeBase?.documentPayments, baseKey, canEditStructure, payments]);

  const handleApplyDefaultSchedule = useCallback(async () => {
    if (!canEditStructure || scheduleBusy || !node?.key || !user?.key) return;
    if (!defaultSchedule.length) {
      toast.info('No default schedule saved yet. Schedule payments, then Set as default.', {
        autoClose: 3500,
      });
      return;
    }
    if (payments.length > 0) {
      toast.error('Clear existing payments before applying the default schedule.', {
        autoClose: 4000,
      });
      return;
    }

    setScheduleBusy(true);
    try {
      const total = Number(rollups.total) || 0;
      for (let index = 0; index < defaultSchedule.length; index += 1) {
        const row = defaultSchedule[index];
        let defaultAmountCents = row.amount;
        if (row.amountBasis === 'percent' && row.percentOfTotal != null) {
          defaultAmountCents = Math.round((total * Number(row.percentOfTotal)) / 100);
        }
        await createDocumentPayment({
          parentNode: node,
          user,
          baseKey: baseKey || null,
          paymentType,
          parentType: nodeType,
          existingCount: index,
          defaultAmountCents,
          percentOfTotal: row.amountBasis === 'percent' ? row.percentOfTotal : null,
          amountBasis: row.amountBasis,
          title: row.title,
          paymentTerms: row.paymentTerms || null,
          sortOrder: (index + 1) * 10,
        });
      }
      scheduleWriteBack();
      toast.success('Default schedule applied.', { autoClose: 3000 });
    } catch (error) {
      toast.error(error?.message || 'Could not apply default schedule.', { autoClose: 4000 });
    } finally {
      setScheduleBusy(false);
    }
  }, [
    baseKey,
    canEditStructure,
    defaultSchedule,
    node,
    nodeType,
    paymentType,
    payments.length,
    rollups.total,
    scheduleBusy,
    scheduleWriteBack,
    user,
  ]);

  if (!node) return null;
  if (previewMode && rows.length === 0) return null;

  const rootClassName = [
    'document-payments',
    previewMode ? 'document-payments-preview' : '',
  ].filter(Boolean).join(' ');

  const tableClassName = [
    'document-payments-table',
    canOpenPayment || canEditStructure || canLog ? 'has-row-actions' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClassName}>
      <div className="document-payments-header">
        <div className="document-payments-title">
          {`Payments (${rows.length})`}
        </div>

        {canEditStructure ? (
          <div className="document-payments-header-actions">
            {payments.length > 0 ? (
              <Button
                label="Set as default"
                iconKey="bookmark"
                height="30px"
                enabled={!scheduleBusy}
                onClick={handleSaveDefaultSchedule}
              />
            ) : (
              <Button
                label="Apply default"
                iconKey="playlist_add"
                height="30px"
                enabled={!scheduleBusy && defaultSchedule.length > 0}
                onClick={handleApplyDefaultSchedule}
              />
            )}
            <Button
              label={scheduleBusy ? 'Scheduling…' : 'Request a Deposit'}
              iconKey="savings"
              height="30px"
              enabled={!scheduleBusy}
              onClick={() => handleSchedule({
                title: 'Deposit',
                amountBasis: 'percent',
                percentOfTotal: 50,
              })}
            />
            <Button
              label={scheduleBusy ? 'Scheduling…' : 'Schedule a Payment'}
              iconKey="add"
              height="30px"
              enabled={!scheduleBusy}
              onClick={() => handleSchedule()}
            />
          </div>
        ) : null}
      </div>

      <div className="document-payments-body">
        {rows.length === 0 ? (
          <div className="document-payments-empty">
            No payments scheduled.
            {canEdit ? ' Request a deposit or schedule installments.' : ''}
          </div>
        ) : (
          <div className="document-payments-scroll">
            <table className={tableClassName}>
              <thead>
                <tr>
                  <th className="document-payments-col-name" scope="col">Payment</th>
                  <th className="document-payments-col-amount" scope="col">Amount</th>
                  {!previewMode ? (
                    <th className="document-payments-col-terms" scope="col">Terms</th>
                  ) : null}
                  <th className="document-payments-col-due" scope="col">Due</th>
                  <th className="document-payments-col-status" scope="col">Status</th>
                  {canEdit || canOpenPayment ? (
                    <th className="document-payments-col-actions" scope="col">
                      <span className="document-payments-sr-only">Actions</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ payment, model }, index) => {
                  const canLogRow = canLog && isDocumentPaymentOpenStatus(model.status);
                  return (
                    <tr key={model.key}>
                      <td className="document-payments-col-name">
                        <span className="document-payments-name">
                          {model.title}
                        </span>
                      </td>
                      <td className="document-payments-col-amount">
                        {formatMoneyCell(model.amount)}
                        {model.amountBasis === 'percent' && model.percentOfTotal != null ? (
                          <span className="document-payments-amount-meta">
                            {`${model.percentOfTotal}% of total`}
                          </span>
                        ) : null}
                      </td>
                      {!previewMode ? (
                        <td className="document-payments-col-terms">
                          {model.paymentTermsLabel || (
                            <span className="document-payments-muted">—</span>
                          )}
                        </td>
                      ) : null}
                      <td className="document-payments-col-due">
                        {model.dueDateLabel || (
                          <span className="document-payments-muted">—</span>
                        )}
                      </td>
                      <td className="document-payments-col-status">
                        <div className="document-payments-status">
                          {statusField && !previewMode ? (
                            <EnumPillSelect
                              field={statusField}
                              value={model.status}
                              node={payment}
                              typeKey={DOCUMENT_PAYMENT_TYPE_KEY}
                              canEdit={canEditStructure}
                              onNodeChange={handlePaymentChange}
                              placeholder="Status…"
                              language={language}
                              fontSize="12px"
                            />
                          ) : (
                            model.statusLabel || (
                              <span className="document-payments-muted">—</span>
                            )
                          )}
                        </div>
                      </td>
                      {canEditStructure || canLogRow || canOpenPayment ? (
                        <td className="document-payments-col-actions">
                          <div className="document-payments-row-actions">
                            {canEditStructure && index > 0 ? (
                              <IconButton
                                iconKey="arrow_upward"
                                title="Move up"
                                size={28}
                                iconSize={16}
                                onClick={() => handleMove(payment, 'up')}
                              />
                            ) : null}
                            {canEditStructure && index < rows.length - 1 ? (
                              <IconButton
                                iconKey="arrow_downward"
                                title="Move down"
                                size={28}
                                iconSize={16}
                                onClick={() => handleMove(payment, 'down')}
                              />
                            ) : null}
                            {canLogRow ? (
                              <IconButton
                                iconKey="payments"
                                title="Log payment"
                                size={28}
                                iconSize={18}
                                onClick={() => setLogPayment(payment)}
                              />
                            ) : null}
                            {canOpenPayment ? (
                              <IconButton
                                iconKey="more_horiz"
                                title="Open payment"
                                size={28}
                                iconSize={18}
                                onClick={() => handleRowOpen(payment)}
                              />
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LogDocumentPaymentModal
        isOpen={Boolean(logPayment)}
        onClose={() => setLogPayment(null)}
        payment={logPayment}
        parentNode={node}
        parentType={nodeType}
        baseKey={baseKey}
        user={user}
        existingCount={payments.length}
        language={language}
        onLogged={handleLogged}
      />
    </div>
  );
});

DocumentPayments.displayName = 'DocumentPayments';

export default DocumentPayments;
